import { useQuery, useMutation, useQueryClient } from 'react-query'
import API from '.'
import { serializeQuery } from './utils'

const ENDPOINT = '/api/lucky-draw'
const QUERY_KEY = 'lucky-draw'

/**
 * Drop the cached draws after a write, rather than invalidating them. Invalidating still serves the
 * stale entry before the refetch lands, and the edit form seeds itself from the first draw it sees
 * — so it would fill with the pre-save version and never re-seed.
 */
const useDropCachedDraws = () => {
  const queryClient = useQueryClient()

  return () => {
    // sources is a static registry, so it is left alone.
    for (const scope of ['list', 'detail', 'entries', 'pool']) {
      queryClient.removeQueries([QUERY_KEY, scope])
    }
  }
}

/** A mutation config that clears the cache on success, then runs whatever the caller passed. */
const withDroppedCache = (config, drop) => ({
  ...config,
  onSuccess: (...args) => {
    drop()
    config.onSuccess?.(...args)
  },
})

export const useGetLuckyDraws = (query = {}, config = {}) =>
  useQuery(
    [QUERY_KEY, 'list', query],
    async () => (await API.get(serializeQuery(ENDPOINT, query))).data,
    config,
  )

export const useGetLuckyDraw = (id, config = {}) =>
  useQuery([QUERY_KEY, 'detail', id], async () => (await API.get(`${ENDPOINT}/${id}`)).data, config)

/**
 * One prize's entry pool. Before its first press this costs an upstream points fetch, so it is
 * kept out of the draw detail.
 */
export const useGetLuckyDrawPool = (id, prizeId, config = {}) =>
  useQuery(
    [QUERY_KEY, 'pool', id, prizeId],
    async () => (await API.get(serializeQuery(`${ENDPOINT}/${id}/pool`, { prizeId }))).data,
    config,
  )

/** Every company in the running, named. Same upstream cost as a pool fetch, so it is opt-in. */
export const useGetLuckyDrawEntries = (id, config = {}) =>
  useQuery(
    [QUERY_KEY, 'entries', id],
    async () => (await API.get(`${ENDPOINT}/${id}/entries`)).data,
    config,
  )

export const useGetLuckyDrawSources = (config = {}) =>
  useQuery([QUERY_KEY, 'sources'], async () => (await API.get(`${ENDPOINT}/sources`)).data, config)

export const usePostLuckyDraw = (config = {}) => {
  const drop = useDropCachedDraws()

  return useMutation((newData) => API.post(ENDPOINT, newData), withDroppedCache(config, drop))
}

export const usePatchLuckyDraw = (config = {}) => {
  const drop = useDropCachedDraws()

  return useMutation(
    (updatedData) => API.patch(ENDPOINT, updatedData),
    withDroppedCache(config, drop),
  )
}

// Takes { id } — the server reads it from the body, not the path.
export const useDeleteLuckyDraw = (config = {}) => {
  const drop = useDropCachedDraws()

  return useMutation((body) => API.delete(ENDPOINT, { data: body }), withDroppedCache(config, drop))
}

export const useRollLuckyDraw = (id, config = {}) =>
  useMutation((data) => API.post(`${ENDPOINT}/${id}/roll`, data), config)

export const useDeleteLuckyDrawWinner = (id, config = {}) =>
  useMutation((winnerId) => API.delete(`${ENDPOINT}/${id}/winners/${winnerId}`), config)

/** Every winner of one prize, in a single request. */
export const useDeleteLuckyDrawPrizeWinners = (id, config = {}) =>
  useMutation(
    (prizeId) => API.delete(serializeQuery(`${ENDPOINT}/${id}/winners`, { prizeId })),
    config,
  )

/**
 * Replace a manual draw's entry list with an uploaded sheet. Drops the cache: the entries tab and
 * the draw's own entry count both change.
 */
export const usePutLuckyDrawEntryList = (id, config = {}) => {
  const drop = useDropCachedDraws()

  return useMutation((file) => {
    const form = new FormData()

    form.append('file', file)

    return API.put(`${ENDPOINT}/${id}/entry-list`, form)
  }, withDroppedCache(config, drop))
}

export const useExportLuckyDraw = (id, config = {}) =>
  useMutation(
    () => API.post(`${ENDPOINT}/${id}/export`, {}, { responseType: 'blob' }),
    config,
  )
